import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatErr } from "@/lib/api";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      if (u?.is_super_admin) navigate("/admin");
      else if (!u?.onboarding_completed) navigate("/onboarding");
      else navigate("/dashboard");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone-100 grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex bg-forest-600 text-white relative">
        <div className="m-auto max-w-md px-12 py-16">
          <Link to="/" className="flex items-center gap-2" data-testid="back-home">
            <Sparkles size={20} strokeWidth={1.5} />
            <span className="font-heading text-2xl">Toothpix</span>
          </Link>
          <h2 className="font-heading text-4xl mt-16 leading-tight">
            Welcome back.
            <br />
            Your patients are waiting.
          </h2>
          <p className="text-bone-100/70 mt-6 leading-relaxed">
            Sign in to see new leads, review smile previews, and update your
            clinic page.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-12">
            <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
            <span className="font-heading text-2xl text-forest-700">Toothpix</span>
          </Link>
          <div className="overline mb-3">Clinic Login</div>
          <h1 className="font-heading text-4xl text-forest-700">Sign in</h1>
          <p className="text-sm text-forest-400 mt-2">
            Don't have an account?{" "}
            <Link to="/signup" data-testid="to-signup" className="text-forest-600 underline underline-offset-4">
              Start a free trial
            </Link>
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <label className="label-flat">Email</label>
              <input
                type="email"
                className="input-flat"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="label-flat">Password</label>
              <input
                type="password"
                className="input-flat"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
              <Link
                to="/forgot-password"
                className="inline-block mt-2 text-xs text-forest-600 underline underline-offset-4"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            {err && (
              <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={14} /></>)}
            </button>
          </div>

          <div className="mt-8 text-xs text-forest-400 leading-relaxed border-t border-bone-200 pt-6">
            <div className="overline mb-2">Demo Account</div>
            demo@brightortho.com · DemoSmile2026!
          </div>
        </form>
      </div>
    </div>
  );
}
